import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useVendorStore } from '../stores/vendorStore';
import { getAllBanks, getPartners, exportBanksByIds } from '../services/api';
import Navbar from '../components/Navbar';
import MobileNavbar from '../components/MobileNavbar';
import BankDetailDialogEnhanced from '../components/BankDetailDialogEnhanced';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Eye, QrCode, Copy, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { VirtualizedBankTable } from '../components/banks/VirtualizedBankTable';

const STATUS_OPTIONS = ['New Bank', 'In Process', 'Unassigned', 'Active', 'Inactive', 'Freezed - Transaction', 'Freezed - CBI', 'Return', 'Closed'];

const STATUS_STYLES = {
  'New Bank': 'bg-sky-100 text-sky-800 border-sky-200',
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'In Process': 'bg-amber-100 text-amber-800 border-amber-200',
  'Unassigned': 'bg-teal-100 text-teal-800 border-teal-200',
  'Inactive': 'bg-slate-100 text-slate-800 border-slate-200',
  'Freezed - Transaction': 'bg-red-100 text-red-800 border-red-200',
  'Freezed - CBI': 'bg-red-100 text-red-800 border-red-200',
  'Return': 'bg-orange-100 text-orange-800 border-orange-200',
  'Closed': 'bg-purple-100 text-purple-800 border-purple-200',
};

const HoverCopyCell = ({ value, displayValue, label }) => {
  const [showFull, setShowFull] = useState(false);
  if (!value) return <span className="text-sm text-slate-400">-</span>;
  return (
    <div className="relative group" onMouseEnter={() => setShowFull(true)} onMouseLeave={() => setShowFull(false)}>
      <span className="text-sm font-mono text-slate-600 cursor-default">{displayValue}</span>
      {showFull && (
        <div className="absolute z-20 left-0 top-full mt-1 bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg flex items-center gap-2 whitespace-nowrap">
          <span className="font-mono">{value}</span>
          <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(value); toast.success(`${label} copied!`); }} className="p-0.5 hover:bg-slate-700 rounded">
            <Copy className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

const maskAccount = (acc) => {
  if (!acc) return '-';
  const str = acc.toString();
  return 'XXXXXX' + str.slice(-4);
};

const BanksPage = () => {
  const user = useAuthStore((s) => s.user);
  const { setSelectedBank, setSelectedVendor, vendors, fetchVendors } = useVendorStore();
  const [allBanks, setAllBanks] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [qrFilter, setQrFilter] = useState('');
  const [showDetail, setShowDetail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [banksRes, partnersRes] = await Promise.all([getAllBanks(), getPartners()]);
      setAllBanks(banksRes.data);
      setPartners(partnersRes.data || []);
    } catch {
      toast.error('Failed to load banks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchVendors();
  }, [fetchData, fetchVendors]);

  const filtered = useMemo(() => allBanks.filter(bank => {
    if (statusFilter && bank.status !== statusFilter) return false;
    if (vendorFilter && bank.vendorId !== vendorFilter) return false;
    if (branchFilter) {
      if (branchFilter === '__unassigned__') {
        if (bank.assignedPartnerId) return false;
      } else if (bank.assignedPartnerId !== branchFilter) return false;
    }
    if (accountTypeFilter && bank.accountType !== accountTypeFilter) return false;
    if (qrFilter === 'with-qr' && bank.qrAvailable !== 'yes') return false;
    if (qrFilter === 'without-qr' && bank.qrAvailable === 'yes') return false;
    if (search) {
      const s = search.toLowerCase();
      return [bank.companyName, bank.bankName, bank.vendorName, bank.accountNumber, bank.region, bank.upi, bank.holderName, bank.ifsc].join(' ').toLowerCase().includes(s);
    }
    return true;
  }), [allBanks, statusFilter, vendorFilter, branchFilter, accountTypeFilter, qrFilter, search]);

  const uniqueVendors = useMemo(() => [...new Map(allBanks.map(b => [b.vendorId, { id: b.vendorId, name: b.vendorName }])).values()], [allBanks]);
  const uniqueAccountTypes = useMemo(() => [...new Set(allBanks.map(b => b.accountType).filter(Boolean))], [allBanks]);
  const branchOptions = useMemo(() => partners.map(p => ({ id: p.id, name: p.name || p.username })), [partners]);

  const handleExport = useCallback(async () => {
    if (filtered.length === 0) { toast.error('No banks match the current filters'); return; }
    setExporting(true);
    try {
      const parts = [];
      if (statusFilter) parts.push(`Status: ${statusFilter}`);
      if (vendorFilter) parts.push(`Vendor: ${uniqueVendors.find(v => v.id === vendorFilter)?.name || vendorFilter}`);
      if (branchFilter) {
        parts.push(`Branch: ${branchFilter === '__unassigned__' ? 'Unassigned' : (branchOptions.find(b => b.id === branchFilter)?.name || branchFilter)}`);
      }
      if (accountTypeFilter) parts.push(`Type: ${accountTypeFilter}`);
      if (qrFilter) parts.push(`QR: ${qrFilter === 'with-qr' ? 'With QR' : 'Without QR'}`);
      if (search) parts.push(`Search: "${search}"`);
      const label = parts.length ? parts.join(', ') : 'All Banks';

      const res = await exportBanksByIds(filtered.map(b => b.id), 'excel', label);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `banks_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filtered.length} banks`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [filtered, statusFilter, vendorFilter, branchFilter, accountTypeFilter, qrFilter, search, uniqueVendors, branchOptions]);

  const statusCounts = useMemo(() => {
    const counts = {};
    allBanks.forEach(b => { counts[b.status] = (counts[b.status] || 0) + 1; });
    return counts;
  }, [allBanks]);

  const vendorBreakdown = useMemo(() =>
    Object.entries(
      filtered.reduce((acc, b) => { acc[b.vendorName] = (acc[b.vendorName] || 0) + 1; return acc; }, {})
    ).sort((a, b) => b[1] - a[1]),
  [filtered]);

  const handleView = (bank) => {
    // Set vendor and bank in store so BankDetailDialogEnhanced can read them
    const vendor = vendors.find(v => v.id === bank.vendorId);
    if (vendor) setSelectedVendor(vendor);
    setSelectedBank(bank);
    setShowDetail(true);
  };

  const handleDetailClose = (open) => {
    setShowDetail(open);
    if (!open) fetchData(); // refresh after edits
  };

  return (
    <>
      <div className="hidden md:block"><Navbar /></div>
      <div className="md:hidden"><MobileNavbar /></div>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 pt-20 md:pt-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="banks-page-title">All Banks</h1>
            <p className="text-sm text-slate-500 mt-1">View and filter all bank accounts across vendors</p>
          </div>
          <Button
            data-testid="banks-export-excel-btn"
            onClick={handleExport}
            disabled={exporting || loading || filtered.length === 0}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            {exporting ? 'Exporting…' : `Export Excel (${filtered.length})`}
          </Button>
        </div>

        {/* Status KPI Cards */}
        <div className="flex gap-3 flex-wrap">
          {[
            { label: 'Total', count: allBanks.length, color: 'text-slate-900 border-slate-200' },
            { label: 'Unassigned', count: statusCounts['Unassigned'] || 0, color: 'text-teal-700 border-teal-200 bg-teal-50' },
            { label: 'Active', count: statusCounts['Active'] || 0, color: 'text-green-700 border-green-200 bg-green-50' },
            { label: 'In Process', count: statusCounts['In Process'] || 0, color: 'text-amber-700 border-amber-200 bg-amber-50' },
            { label: 'New Bank', count: statusCounts['New Bank'] || 0, color: 'text-sky-700 border-sky-200 bg-sky-50' },
            { label: 'Freezed', count: (statusCounts['Freezed - Transaction'] || 0) + (statusCounts['Freezed - CBI'] || 0), color: 'text-red-700 border-red-200 bg-red-50' },
            { label: 'Return', count: statusCounts['Return'] || 0, color: 'text-orange-700 border-orange-200 bg-orange-50' },
          ].map(kpi => (
            <Card
              key={kpi.label}
              className={`px-4 py-2 cursor-pointer border transition-all hover:shadow-md ${kpi.color} ${statusFilter === kpi.label ? 'ring-2 ring-offset-1 ring-indigo-400' : ''}`}
              onClick={() => {
                if (kpi.label === 'Total') setStatusFilter('');
                else if (kpi.label === 'Freezed') setStatusFilter(statusFilter === 'Freezed - Transaction' ? '' : 'Freezed - Transaction');
                else setStatusFilter(statusFilter === kpi.label ? '' : kpi.label);
              }}
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="text-xs font-medium opacity-80">{kpi.label}</div>
              <div className="text-xl font-bold">{kpi.count}</div>
            </Card>
          ))}
        </div>

        {/* Vendor-wise breakdown for selected filter */}
        {filtered.length > 0 && (
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs font-semibold text-slate-500 mr-1">Vendor-wise:</span>
            {vendorBreakdown.map(([vendor, count]) => (
                <Badge key={vendor} variant="outline" className="text-xs gap-1 py-1 cursor-pointer hover:bg-slate-100" onClick={() => {
                  const vid = allBanks.find(b => b.vendorName === vendor)?.vendorId;
                  if (vid) setVendorFilter(vendorFilter === vid ? '' : vid);
                }} data-testid={`vendor-count-${vendor}`}>
                  {vendor}: <strong>{count}</strong>
                </Badge>
              ))
            }
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search company, bank, vendor, account, UPI, region..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="banks-search" />
          </div>
          <Select value={statusFilter || "all-status"} onValueChange={(val) => setStatusFilter(val === "all-status" ? "" : val)}>
            <SelectTrigger className="w-40" data-testid="banks-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all-status">All Status</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vendorFilter || "all-vendors"} onValueChange={(val) => setVendorFilter(val === "all-vendors" ? "" : val)}>
            <SelectTrigger className="w-40" data-testid="banks-vendor-filter"><SelectValue placeholder="Vendor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all-vendors">All Vendors</SelectItem>
              {uniqueVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={branchFilter || "all-branches"} onValueChange={(val) => setBranchFilter(val === "all-branches" ? "" : val)}>
            <SelectTrigger className="w-40" data-testid="banks-branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all-branches">All Branches</SelectItem>
              <SelectItem value="__unassigned__">Unassigned</SelectItem>
              {branchOptions.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={accountTypeFilter || "all-types"} onValueChange={(val) => setAccountTypeFilter(val === "all-types" ? "" : val)}>
            <SelectTrigger className="w-40" data-testid="banks-type-filter"><SelectValue placeholder="Account Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all-types">All Types</SelectItem>
              {uniqueAccountTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={qrFilter || "all-qr"} onValueChange={(val) => setQrFilter(val === "all-qr" ? "" : val)}>
            <SelectTrigger className="w-36" data-testid="banks-qr-filter"><SelectValue placeholder="QR" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all-qr">All QR</SelectItem>
              <SelectItem value="with-qr">With QR</SelectItem>
              <SelectItem value="without-qr">Without QR</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <div className="text-sm text-slate-500">Showing {filtered.length} of {allBanks.length} banks</div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading banks...</div>
            ) : filtered.length > 200 ? (
              <VirtualizedBankTable banks={filtered} onView={handleView} height={600} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Company Name</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Vendor</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Account</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">UPI</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Account Type</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">QR</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600">Status</th>
                      <th className="text-center p-3 text-xs font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((bank) => (
                      <tr key={bank.id} className="border-b hover:bg-slate-50" data-testid={`all-bank-row-${bank.id}`}>
                        <td className="p-3">
                          <div className="font-medium text-sm">{bank.companyName || bank.bankName}</div>
                          {bank.companyName && <div className="text-xs text-slate-500">{bank.bankName}</div>}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs font-medium">{bank.vendorName}</Badge>
                        </td>
                        <td className="p-3">
                          <HoverCopyCell value={bank.accountNumber} displayValue={maskAccount(bank.accountNumber)} label="Account" />
                        </td>
                        <td className="p-3">
                          <HoverCopyCell value={bank.upi} displayValue={bank.upi ? '***' + bank.upi.slice(-6) : '-'} label="UPI" />
                        </td>
                        <td className="p-3 text-sm text-slate-600">{bank.accountType || 'Savings'}</td>
                        <td className="p-3">
                          {bank.qrAvailable === 'yes' ? (
                            <div className="inline-flex items-center gap-1">
                              <QrCode className="w-4 h-4 text-green-600" />
                              <span className="text-xs text-green-700 font-medium">Yes</span>
                              {bank.qrDataUrl && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(bank.qrDataUrl); toast.success('QR copied!'); }}
                                  className="inline-flex items-center gap-1 text-xs bg-slate-800 text-white px-2 py-0.5 rounded ml-1 hover:bg-slate-900"
                                >
                                  <Copy className="w-3 h-3" /> Copy
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">No</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge className={STATUS_STYLES[bank.status] || 'bg-slate-100 text-slate-800'}>{bank.status}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          <Button onClick={() => handleView(bank)} variant="outline" size="sm" className="gap-1" data-testid={`view-all-bank-${bank.id}`}>
                            <Eye className="w-3 h-3" /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && <div className="p-8 text-center text-slate-500">No banks match the current filters</div>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <BankDetailDialogEnhanced open={showDetail} onOpenChange={handleDetailClose} />
    </>
  );
};

export default BanksPage;
